import React from 'react';
import type { WaitlistEntry } from 'types/waitlist';

import useWaitlistCountdown from './useWaitlistCountdown';
import styles from './WaitlistChip.module.scss';

interface WaitlistChipProps {
  entry: WaitlistEntry;
  // Tap while still queued → observe/switch to the table.
  onOpen?: (gameId: number, gameType: string) => void;
  // Tap when the seat is ready → claim it.
  onClaim?: (entry: WaitlistEntry) => void;
}

/**
 * Compact waitlist representation for the in-game table nav row — replaces the
 * disruptive seat-offer modal and the card-fan "WAITLIST" stamp. While queued
 * it shows "WAIT · N" (position); when the seat is ready it shows a live,
 * pulsing "{secs} JOIN" countdown so a waiting player sees the accept window
 * regardless of whether their tables are stacked or spread.
 */
const WaitlistChip: React.FC<WaitlistChipProps> = ({ entry, onOpen, onClaim }) => {
  const { secondsRemaining, active } = useWaitlistCountdown({
    waitForAcceptStartTime: entry.waitForAcceptStartTime,
    waitlistTimeOut: entry.waitlistTimeOut,
    bumpCount: entry.bumpCount
  });
  const ready = entry.yourTurn && entry.availableSeats > 0;

  if (ready) {
    return (
      <button
        type='button'
        className={`${styles.chip} ${styles.join}`}
        onClick={() => onClaim?.(entry)}
        aria-label={`join-ready-seat-${entry.gameId}`}>
        <span className={styles.count}>{active ? secondsRemaining : 0}</span>
        <span>JOIN</span>
      </button>
    );
  }

  return (
    <button
      type='button'
      className={`${styles.chip} ${styles.wait}`}
      onClick={() => onOpen?.(entry.gameId, entry.gameType)}
      aria-label={`waitlisted-seat-${entry.gameId}`}>
      <span>WAIT</span>
      {entry.position > 0 && <span className={styles.pos}>· {entry.position}</span>}
    </button>
  );
};

export default WaitlistChip;
